import { all, call, put, takeLatest } from '@redux-saga/core/effects';
import axios, { AxiosResponse } from 'axios';

import { SagaPayload } from '../api.types';
import {
  CreateSupportTemplateRequest,
  CreateSupportTemplateResponse,
  UpdateSupportTemplateRequest,
  UpdateSupportTemplateResponse,
  ViewSupportTemplateRequest,
  ViewSupportTemplateResponse
} from './support-template.types';

import {
  CREATING_SUPPORT_TEMPLATE,
  RETRIEVING_SUPPORT_TEMPLATE,
  UPDATING_SUPPORT_TEMPLATE
} from './support-template.constants';

import { requestViewAllProgrammes } from '../programme/v2/programme.actions';
import {
  requestCreateSupportTemplate,
  requestUpdateSupportTemplate,
  requestViewSupportTemplate
} from './support-template.actions';

import {
  getCreateSupportTemplateRequest,
  getUpdateSupportTemplateRequest,
  getViewSupportTemplateRequest
} from './support-template.endpoints';

import { setSupportTemplate } from '../../slices/programme/support/support.slice';
import { addSpinner, addSystemNotice, removeSpinner } from '../../slices/system/system.slice';

import { getErrorMessage } from '../../utils/api.util';
import { ERROR, SUCCESS } from '../../utils/constants';

export function* performCreateSupportTemplateRequest({ payload }: SagaPayload<CreateSupportTemplateRequest>) {
  const { requestData, onSuccess } = payload;

  try {
    yield put(addSpinner(CREATING_SUPPORT_TEMPLATE));

    const [endpoint, options] = getCreateSupportTemplateRequest(requestData);

    const { data }: AxiosResponse<CreateSupportTemplateResponse> = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess, undefined);
    }

    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
    yield put(
      requestViewSupportTemplate({
        requestData: { programmeExternalReference: requestData.programmeExternalReference }
      })
    );
    yield put(requestViewAllProgrammes());
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(CREATING_SUPPORT_TEMPLATE));
  }
}

export function* watchForCreateSupportTemplateRequest() {
  yield takeLatest(requestCreateSupportTemplate.type, performCreateSupportTemplateRequest);
}

export function* performViewSupportTemplateRequest({ payload }: SagaPayload<ViewSupportTemplateRequest>) {
  const { requestData, onSuccess } = payload;

  try {
    yield put(addSpinner(RETRIEVING_SUPPORT_TEMPLATE));

    const [endpoint, options] = getViewSupportTemplateRequest(requestData);

    const { data }: AxiosResponse<ViewSupportTemplateResponse> = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess, undefined);
    }

    yield put(setSupportTemplate(data.supportTemplates[0]));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(RETRIEVING_SUPPORT_TEMPLATE));
  }
}

export function* watchForViewSupportTemplateRequest() {
  yield takeLatest(requestViewSupportTemplate.type, performViewSupportTemplateRequest);
}

export function* performUpdateSupportTemplateRequest({ payload }: SagaPayload<UpdateSupportTemplateRequest>) {
  const { requestData, onSuccess } = payload;

  try {
    yield put(addSpinner(UPDATING_SUPPORT_TEMPLATE));

    const [endpoint, options] = getUpdateSupportTemplateRequest(requestData);

    const { data }: AxiosResponse<UpdateSupportTemplateResponse> = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess, undefined);
    }

    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
    yield put(
      requestViewSupportTemplate({
        requestData: { programmeExternalReference: requestData.programmeExternalReference }
      })
    );
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(UPDATING_SUPPORT_TEMPLATE));
  }
}

export function* watchForUpdateSupportTemplateRequest() {
  yield takeLatest(requestUpdateSupportTemplate.type, performUpdateSupportTemplateRequest);
}

export default function* supportTemplateSaga() {
  yield all([
    watchForCreateSupportTemplateRequest(),
    watchForViewSupportTemplateRequest(),
    watchForUpdateSupportTemplateRequest()
  ]);
}
